import { defineStore } from 'pinia';
import xpMessengerService from '@/services/xpMessengerService';

/**
 * Store for managing XP Messenger state
 * @returns {Object} XP Messenger store
 */
export const useXPMessengerStore = defineStore('xpMessenger', {
  state: () => ({
    partners: [],
    loading: false,
    error: null,
    testResult: null,
    selectedPartner: null,
    certificateRequestResult: null, // Store the certificate request result
    formErrors: {
      backendId: null,
      displayName: null
    }
  }),

  actions: {


    /**
     * Test the XP Messenger API connection
     * @param {Object} $log - Logger instance
     */
    async testConnection($log) {
      this.loading = true;
      this.error = null;
      this.testResult = null;

      try {
        $log.debug('Testing XP Messenger API connection...');
        this.testResult = await xpMessengerService.test($log);
        $log.debug('Test successful:', this.testResult);
      } catch (error) {
        $log.error('Test failed:', error);
        this.error = error.message || 'Failed to test connection';
      } finally {
        this.loading = false;
      }
    },

    /**
     * Fetch partners from the XP Messenger API
     * @param {Object} $log - Logger instance
     */
    async fetchPartners($log) {

      this.loading = true;
      this.error = null;

      try {
        $log.debug(`Fetching partners`);
        this.partners = await xpMessengerService.getPartners($log);
        $log.debug(`Successfully fetched ${this.partners.length} partners`);
      } catch (error) {
        $log.error('Error fetching partners:', error);
        this.error = error.message || 'Failed to fetch partners';

        // Extract more detailed error information if available
        if (error.response && error.response.data) {
          $log.error('Error response data:', error.response.data);
          this.error = error.response.data.message || this.error;
        }
      } finally {
        this.loading = false;
      }
    },

    /**
     * Get a specific partner by ID
     * @param {string} partnerId - The ID of the partner to fetch
     * @param {Object} $log - Logger instance
     * @throws {Error} If partnerId is not provided or invalid
     */
    async getPartner(partnerId, $log) {

      // Validate partnerId
      if (!partnerId) {
        const error = new Error('Partner ID is required');
        $log.error(error.message);
        this.error = error.message;
        return;
      }

      if (typeof partnerId !== 'string') {
        const error = new Error('Partner ID must be a string');
        $log.error(error.message);
        this.error = error.message;
        return;
      }

      this.loading = true;
      this.error = null;
      this.selectedPartner = null;

      try {
        $log.debug(`Fetching partner ${partnerId}`);
        this.selectedPartner = await xpMessengerService.getPartner(partnerId, $log);
        $log.debug('Successfully fetched partner:', this.selectedPartner);
      } catch (error) {
        $log.error('Error fetching partner:', error);
        this.error = error.message || 'Failed to fetch partner';

        // Extract more detailed error information if available
        if (error.response && error.response.data) {
          $log.error('Error response data:', error.response.data);
          this.error = error.response.data.message || this.error;
        }
      } finally {
        this.loading = false;
      }
    },

    /**
     * Validates partner form data
     * @param {Object} partnerData - The partner data to validate
     * @param {Object} $log - Logger instance
     * @returns {boolean} Whether the data is valid
     */
    validatePartnerForm(partnerData, $log) {
      this.formErrors = {
        backendId: null,
        displayName: null
      };

      let isValid = true;

      // Validate Backend ID
      if (!partnerData.backendId) {
        this.formErrors.backendId = 'Backend ID is required';
        isValid = false;
      } else {
        // Normalize Backend ID to uppercase
        partnerData.backendId = partnerData.backendId.toUpperCase();

        if (!/^[A-Z]{2}\d{6}$/.test(partnerData.backendId)) {
          this.formErrors.backendId = 'Backend ID must be 2 letters followed by 6 digits';
          isValid = false;
        }
      }

      // Validate Display Name
      if (!partnerData.displayName) {
        this.formErrors.displayName = 'Display name is required';
        isValid = false;
      } else if (partnerData.displayName.length < 3) {
        this.formErrors.displayName = 'Display name must be at least 3 characters';
        isValid = false;
      }

      if (!isValid) {
        $log.warn('Form validation failed:', this.formErrors);
      }

      return isValid;
    },

    /**
     * Submit partner creation form
     * @param {Object} partnerData - The partner data to submit
     * @param {Object} $log - Logger instance
     * @returns {Promise<Object>} The created partner data
     * @throws {Error} If validation fails or API call fails
     */
    async submitPartnerForm(partnerData, $log) {
      // Validate form data
      if (!this.validatePartnerForm(partnerData, $log)) {
        throw new Error('Form validation failed');
      }

      this.loading = true;
      this.error = null;

      try {
        $log.debug(`Creating partner`);
        const result = await xpMessengerService.createPartner(partnerData, $log);
        // Refresh partners list after creation
        await this.fetchPartners($log);
        $log.debug('Successfully created partner:', result);
        return result;
      } catch (error) {
        $log.error('Error creating partner:', error);
        this.error = error.message || 'Failed to create partner';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Validates certificate request form data
     * @param {Object} certificateData - The certificate data to validate
     * @param {Object} $log - Logger instance
     * @returns {boolean} Whether the data is valid
     */
    validateCertificateForm(certificateData, $log) {
      this.formErrors = {
        backendId: null,
        displayName: null,
        password: null
      };

      let isValid = true;

      // Validate Backend ID
      if (!certificateData.backendId) {
        this.formErrors.backendId = 'Backend ID is required';
        isValid = false;
      } else {
        // Normalize Backend ID to uppercase
        certificateData.backendId = certificateData.backendId.toUpperCase();

        if (!/^[A-Z]{2}\d{6}$/.test(certificateData.backendId)) {
          this.formErrors.backendId = 'Backend ID must be 2 letters followed by 6 digits';
          isValid = false;
        }
      }

      // Validate Display Name
      if (!certificateData.displayName) {
        this.formErrors.displayName = 'Display name is required';
        isValid = false;
      } else if (certificateData.displayName.length < 3) {
        this.formErrors.displayName = 'Display name must be at least 3 characters';
        isValid = false;
      }

      // Validate Password
      if (!certificateData.password) {
        this.formErrors.password = 'Password is required';
        isValid = false;
      }

      if (!isValid) {
        $log.warn('Certificate form validation failed:', this.formErrors);
      }

      return isValid;
    },

    /**
     * Submit certificate request form
     * @param {string} partnerId - The ID of the partner to request certificate for
     * @param {Object} certificateData - The certificate data to submit
     * @param {Object} $log - Logger instance
     * @returns {Promise<Object>} The certificate request response
     * @throws {Error} If validation fails or API call fails
     */
    async submitCertificateRequest(partnerId, certificateData, $log) {
      // Validate form data
      if (!this.validateCertificateForm(certificateData, $log)) {
        throw new Error('Form validation failed');
      }

      this.loading = true;
      this.error = null;
      this.certificateRequestResult = null; // Reset previous result

      try {
        $log.debug(`Requesting certificate for partner ${partnerId}`);
        const result = await xpMessengerService.requestCertificate(partnerId, certificateData, $log);

        // Store the certificate request result
        this.certificateRequestResult = {
          id: result.id,
          subject: result.subject,
          keyPairAlgo: result.keyPairAlgo,
          certificateRequest: result.certificateRequest
        };

        return result;
      } catch (error) {
        $log.error('Error requesting certificate:', error);
        this.error = error.message || 'Failed to request certificate';
        throw error;
      } finally {
        this.loading = false;
      }
    }
  },

  persist: true, // Persist the store state in localStorage
});
